/** Used when the backend offers no timing, so callers need no null checks. */
export const NULL_GPU_TIMER = {
    available: false,
    begin() { },
    end() { },
    poll() { },
    averageMs: 0,
};
/** Frames averaged for the displayed figure. */
const SAMPLE_WINDOW = 30;
const NANOSECONDS_PER_MS = 1000000;
/**
 * WebGL implementation. WebGL2 exposes the query calls on the context itself
 * while WebGL1 puts them on the extension object, so each operation picks
 * whichever pair exists.
 */
export class WebGLGpuTimer {
    constructor(gl) {
        this.pending = [];
        this.samples = [];
        this.average = 0;
        this.open = null;
        this.gl = gl;
        this.extension =
            gl.getExtension('EXT_disjoint_timer_query_webgl2') ??
                gl.getExtension('EXT_disjoint_timer_query');
    }
    get available() {
        return this.extension !== null;
    }
    get averageMs() {
        return this.average;
    }
    begin() {
        if (!this.extension || this.open) {
            return;
        }
        if (this.gl.createQuery) {
            const query = this.gl.createQuery();
            this.gl.beginQuery(this.extension.TIME_ELAPSED_EXT, query);
            this.open = query;
        }
        else if (this.extension.createQueryEXT && this.extension.beginQueryEXT) {
            const query = this.extension.createQueryEXT();
            this.extension.beginQueryEXT(this.extension.TIME_ELAPSED_EXT, query);
            this.open = query;
        }
    }
    end() {
        if (!this.extension || !this.open) {
            return;
        }
        if (this.gl.endQuery) {
            this.gl.endQuery(this.extension.TIME_ELAPSED_EXT);
        }
        else if (this.extension.endQueryEXT) {
            this.extension.endQueryEXT(this.extension.TIME_ELAPSED_EXT);
        }
        this.pending.push(this.open);
        this.open = null;
    }
    poll() {
        if (!this.extension) {
            return;
        }
        // A disjoint event means the GPU was interrupted and every in-flight
        // timing is meaningless, so the whole batch is discarded rather than
        // averaged in.
        const disjoint = this.gl.getParameter(this.extension.GPU_DISJOINT_EXT);
        for (let i = this.pending.length - 1; i >= 0; i--) {
            const query = this.pending[i];
            const result = this.readQuery(query);
            if (result === null) {
                continue;
            }
            if (!disjoint) {
                this.record(result / NANOSECONDS_PER_MS);
            }
            this.deleteQuery(query);
            this.pending.splice(i, 1);
        }
    }
    readQuery(query) {
        if (this.gl.getQueryParameter) {
            if (!this.gl.getQueryParameter(query, this.gl.QUERY_RESULT_AVAILABLE)) {
                return null;
            }
            return this.gl.getQueryParameter(query, this.gl.QUERY_RESULT);
        }
        const ext = this.extension;
        if (!ext?.getQueryObjectEXT) {
            return null;
        }
        if (!ext.getQueryObjectEXT(query, ext.QUERY_RESULT_AVAILABLE_EXT)) {
            return null;
        }
        return ext.getQueryObjectEXT(query, ext.QUERY_RESULT_EXT);
    }
    deleteQuery(query) {
        if (this.gl.deleteQuery) {
            this.gl.deleteQuery(query);
        }
        else {
            this.extension?.deleteQueryEXT?.(query);
        }
    }
    record(timeMs) {
        this.samples.push(timeMs);
        if (this.samples.length > SAMPLE_WINDOW) {
            this.samples.shift();
        }
        this.average = this.samples.reduce((total, value) => total + value, 0) / this.samples.length;
    }
}
/**
 * WebGPU implementation.
 *
 * three brackets the render pass itself when the renderer is built with
 * `trackTimestamp: true`, so `begin`/`end` have nothing to do. The work is all
 * in `poll`: resolving timestamps is asynchronous, and asking for a second
 * resolve while the first is in flight both wastes work and can interleave
 * results, so only one request is ever outstanding.
 */
export class WebGPUGpuTimer {
    constructor(renderer) {
        this.samples = [];
        this.average = 0;
        this.resolving = false;
        this.renderer = renderer;
        // 'timestamp-query' is an optional WebGPU feature; adapters may omit it,
        // and browsers gate it behind precision settings. Without it three still
        // renders, it just never reports a timestamp.
        this.supported = renderer.hasFeature?.('timestamp-query') === true;
    }
    get available() {
        return this.supported;
    }
    get averageMs() {
        return this.average;
    }
    begin() { }
    end() { }
    poll() {
        if (!this.supported || this.resolving || !this.renderer.resolveTimestampsAsync) {
            return;
        }
        this.resolving = true;
        this.renderer
            .resolveTimestampsAsync()
            .then(elapsed => {
            // The promise resolves with the duration in milliseconds; older three
            // revisions only update renderer.info, so fall back to that.
            const timeMs = elapsed ?? this.renderer.info?.render?.timestamp;
            if (typeof timeMs === 'number' && timeMs > 0) {
                this.record(timeMs);
            }
        })
            .catch(() => {
            // A rejected resolve means the backend withdrew timing support (device
            // lost, feature disabled mid-run). Stop asking rather than spinning.
            this.supported = false;
        })
            .finally(() => {
            this.resolving = false;
        });
    }
    record(timeMs) {
        this.samples.push(timeMs);
        if (this.samples.length > SAMPLE_WINDOW) {
            this.samples.shift();
        }
        this.average = this.samples.reduce((total, value) => total + value, 0) / this.samples.length;
    }
}
/**
 * Builds the timer for the renderer in use: timestamp queries for WebGPU, the
 * disjoint-timer extension for WebGL, and the null timer when neither backend
 * can measure anything.
 */
export function createGpuTimer(renderer) {
    const candidate = renderer;
    if (candidate.isWebGPURenderer === true) {
        const timer = new WebGPUGpuTimer(candidate);
        return timer.available ? timer : NULL_GPU_TIMER;
    }
    const getContext = renderer.getContext;
    if (typeof getContext !== 'function') {
        return NULL_GPU_TIMER;
    }
    const context = getContext.call(renderer);
    if (typeof WebGLRenderingContext !== 'undefined' &&
        context instanceof WebGLRenderingContext === false &&
        (typeof WebGL2RenderingContext === 'undefined' ||
            context instanceof WebGL2RenderingContext === false)) {
        return NULL_GPU_TIMER;
    }
    const timer = new WebGLGpuTimer(context);
    return timer.available ? timer : NULL_GPU_TIMER;
}
